import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import Navigation from "@/components/Navigation";
import { 
  ArrowUpRight, 
  ArrowDownLeft,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Search,
  Filter,
  RefreshCw,
  ArrowLeftRight,
  Calendar,
  DollarSign,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pending' },
    processing: { color: 'bg-blue-100 text-blue-700', icon: Loader2, label: 'Processing' },
    completed: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Completed' },
    paid: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Paid' },
    failed: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Failed' },
    cancelled: { color: 'bg-gray-100 text-gray-700', icon: XCircle, label: 'Cancelled' },
    expired: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Expired' }
  };

  const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
};

export default function TransactionHistoryPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchTransactions();
    }
  }, [user]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Fetch from both Guardarian and Triple-A
      const [guardarianRes, tripleaRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/guardarian/transactions/user/${user.id}`).catch(() => null),
        fetch(`${BACKEND_URL}/api/triplea/payments/user/${user.id}`).catch(() => null)
      ]);

      let allTransactions = [];

      if (guardarianRes?.ok) {
        const guardarianData = await guardarianRes.json();
        if (guardarianData.success && guardarianData.transactions) {
          allTransactions = [
            ...allTransactions,
            ...guardarianData.transactions.map(tx => ({
              ...tx,
              provider: 'guardarian',
              type: 'on-ramp',
              displayAmount: tx.fiat_amount,
              displayCurrency: tx.fiat_currency
            }))
          ];
        }
      }

      if (tripleaRes?.ok) {
        const tripleaData = await tripleaRes.json();
        if (tripleaData.success && tripleaData.payments) {
          allTransactions = [
            ...allTransactions,
            ...tripleaData.payments.map(tx => ({
              ...tx,
              provider: 'triplea',
              type: 'off-ramp',
              displayAmount: tx.amount,
              displayCurrency: tx.currency
            }))
          ];
        }
      }

      // Also check localStorage for pending transfers
      const pendingTransfers = JSON.parse(localStorage.getItem('pending_transfers') || '[]');
      if (pendingTransfers.length > 0) {
        const localTransactions = pendingTransfers.map(tx => ({
          ...tx,
          provider: 'local',
          type: 'transfer',
          status: 'pending',
          displayAmount: tx.fiat_amount,
          displayCurrency: tx.fiat_currency
        }));
        allTransactions = [...allTransactions, ...localTransactions];
      }

      // Sort by date (newest first)
      allTransactions.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTransactions();
    setRefreshing(false);
    toast.success('Transactions refreshed');
  };

  const filteredTransactions = transactions.filter(tx => {
    // Search filter
    const searchMatch = 
      searchTerm === '' ||
      tx.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.recipient_info?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    const statusMatch = 
      filter === 'all' ||
      (filter === 'completed' && ['completed', 'paid'].includes(tx.status?.toLowerCase())) ||
      (filter === 'pending' && ['pending', 'processing'].includes(tx.status?.toLowerCase())) ||
      (filter === 'failed' && ['failed', 'cancelled', 'expired'].includes(tx.status?.toLowerCase()));

    return searchMatch && statusMatch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-gray-50 via-slate-50 to-zinc-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Transaction History
              </h1>
              <p className="text-gray-600">
                View all your transfers and payments
              </p>
            </div>
            <Button 
              onClick={() => navigate('/send-money')}
              className="mt-4 md:mt-0 bg-emerald-600 hover:bg-emerald-700"
            >
              <ArrowUpRight className="w-4 h-4 mr-2" /> New Transfer
            </Button>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by ID, recipient..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Status Filter */}
                <div className="flex gap-2">
                  {['all', 'completed', 'pending', 'failed'].map((status) => (
                    <Button
                      key={status}
                      variant={filter === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilter(status)}
                      className={filter === status ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>

                {/* Refresh */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Transactions List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-emerald-600" />
                Transfers
              </CardTitle>
              <CardDescription>
                {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : filteredTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <ArrowLeftRight className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">No transactions found</h3>
                  <p className="text-gray-500 mb-4">
                    {searchTerm || filter !== 'all' 
                      ? 'Try adjusting your search or filter'
                      : "You haven't made any transfers yet"}
                  </p>
                  <Button onClick={() => navigate('/send-money')} className="bg-emerald-600 hover:bg-emerald-700">
                    Make Your First Transfer
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTransactions.map((tx, index) => (
                    <div 
                      key={tx.order_id || tx.guardarian_id || tx.payment_reference || index}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.type === 'on-ramp' ? 'bg-blue-100' : 'bg-emerald-100'
                        }`}>
                          {tx.type === 'on-ramp' ? (
                            <ArrowUpRight className="w-5 h-5 text-blue-600" />
                          ) : (
                            <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                          )}
                        </div>

                        {/* Details */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {tx.recipient_info?.name || tx.recipient_name || 'Transfer'}
                            </span>
                            <StatusBadge status={tx.status} />
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(tx.created_at)}
                            </span>
                            {(tx.recipient_info?.country || tx.recipient_country) && (
                              <span className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {tx.recipient_info?.country || tx.recipient_country}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {tx.order_id || tx.guardarian_id || tx.payment_reference}
                          </div>
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {tx.displayAmount?.toFixed?.(2) || tx.displayAmount} {tx.displayCurrency}
                        </div>
                        {tx.crypto_currency && (
                          <div className="text-sm text-gray-500">
                            via {tx.crypto_currency}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Card */}
          {transactions.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Sent</p>
                      <p className="text-xl font-bold text-gray-900">
                        ${transactions.reduce((sum, tx) => sum + (parseFloat(tx.displayAmount) || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <ArrowUpRight className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Transfers</p>
                      <p className="text-xl font-bold text-gray-900">{transactions.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Completed</p>
                      <p className="text-xl font-bold text-gray-900">
                        {transactions.filter(tx => ['completed', 'paid'].includes(tx.status?.toLowerCase())).length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
